import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';
import { useAuth } from '../../context/AuthContext';
import { isOnFreePlan, isSubscriptionActive } from '../../utils/subscriptionStatus';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import PlanFeatureMatrix from './PlanFeatureMatrix';
import PlanPricingCard from './PlanPricingCard';
import BillingCycleComparison from './BillingCycleComparison';
import { sellingPriceForCycle, formatBillingCycleLabel } from '../../utils/planPricing';

export default function PlanComparison() {
  const { toast } = useToast();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlanName, setCurrentPlanName] = useState(null);
  const [currentPlanSlug, setCurrentPlanSlug] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [crmSuggestion, setCrmSuggestion] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const selectedPlanRef = useRef(null);

  const hasActiveSub = isSubscriptionActive(user)
    || ['active', 'trial'].includes(subscriptionStatus);

  useEffect(() => {
    refreshUser?.();
    api.get('subscription/plans/compare')
      .then((res) => {
        const loadedPlans = res.data?.data?.plans || [];
        const suggestion = res.data?.data?.crm_suggestion || null;
        const status = res.data?.data?.subscription_status || null;
        setPlans(loadedPlans);
        setCurrentPlanId(res.data?.data?.current_plan_id);
        setCurrentPlanName(res.data?.data?.current_plan_name);
        setCurrentPlanSlug(res.data?.data?.current_plan_slug);
        setSubscriptionStatus(status);
        setCrmSuggestion(suggestion);

        const activeFromApi = ['active', 'trial'].includes(status);
        if (suggestion?.plan_id && suggestion.payment_status !== 'paid' && !activeFromApi) {
          setSelectedPlanId(suggestion.plan_id);
          setSelectedCycle(suggestion.billing_cycle || 'monthly');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedPlanId || loading) return;
    selectedPlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedPlanId, loading]);

  const openBillingComparison = (plan, action) => {
    setSelectedPlanId(plan.id);
    setPendingPlan(plan);
    setPendingAction(action);
    setConfirmPlan(null);
    setPreview(null);
  };

  const closeBillingComparison = () => {
    setPendingPlan(null);
    setPendingAction(null);
  };

  const handleCycleSelect = async (cycle) => {
    if (!pendingPlan) return;
    setSelectedCycle(cycle);
    setConfirmPlan({ plan: pendingPlan, action: pendingAction, cycle });
    setPendingPlan(null);
    setPendingAction(null);
    try {
      const res = await api.post('subscription/upgrade/preview', {
        plan_id: pendingPlan.id,
        billing_cycle: cycle,
      });
      setPreview(res.data?.data || null);
    } catch {
      setPreview(null);
    }
  };

  const closeConfirm = () => {
    setConfirmPlan(null);
    setPreview(null);
  };

  const checkout = async () => {
    if (!confirmPlan) return;
    setCheckingOut(true);
    try {
      const { plan, action, cycle } = confirmPlan;
      const res = await api.post('subscription/checkout', {
        plan_id: plan.id,
        billing_cycle: cycle,
        action,
        auto_renew: false,
      });
      const checkoutData = res.data?.data;
      if (checkoutData?.activated) {
        await refreshUser?.();
        toast({ title: 'Plan activated', description: 'Your subscription is now active.' });
        closeConfirm();
        navigate('/settings/subscription');
        return;
      }
      if (!checkoutData?.order_id || !checkoutData?.key_id) {
        toast({ title: 'Error', description: 'Could not start checkout', variant: 'destructive' });
        return;
      }

      await openRazorpayCheckout(checkoutData, {
        name: user?.name || user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || user?.mobile || '',
        onSuccess: async (payload) => {
          await api.post('subscription/payments/verify', payload);
          await refreshUser?.();
          toast({ title: 'Payment successful', description: 'Your subscription is now active.' });
          closeConfirm();
          navigate('/settings/subscription');
        },
      });
    } catch (e) {
      if (e?.message !== 'Payment cancelled') {
        toast({
          title: 'Error',
          description: e.response?.data?.message || e.message || 'Checkout failed',
          variant: 'destructive',
        });
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const getActionLabel = (plan) => {
    if (!currentPlanId) return 'Subscribe';
    const currentPlan = plans.find((p) => p.id === currentPlanId);
    if (!currentPlan) return 'Select Plan';
    const currentPrice = sellingPriceForCycle(currentPlan, 'monthly');
    const planPrice = sellingPriceForCycle(plan, 'monthly');
    return planPrice > currentPrice ? 'Upgrade' : 'Select Plan';
  };

  const suggestedPlan = crmSuggestion?.plan
    || plans.find((p) => String(p.id) === String(crmSuggestion?.plan_id));
  const suggestedCycle = crmSuggestion?.billing_cycle || 'monthly';
  const paymentPending = !hasActiveSub
    && crmSuggestion?.payment_status
    && crmSuggestion.payment_status !== 'paid';
  const showFreePlanCard = hasActiveSub && (
    isOnFreePlan(user)
    || user?.subscription?.plan_slug === 'free'
    || currentPlanSlug === 'free'
  );

  const selectedPlan = plans.find((p) => String(p.id) === String(selectedPlanId))
    || suggestedPlan;

  const continueWithSelected = () => {
    if (!selectedPlan) return;
    const cycle = selectedCycle || suggestedCycle;
    setConfirmPlan({
      plan: selectedPlan,
      action: currentPlanId ? 'upgrade' : 'subscribe',
      cycle,
    });
    api.post('subscription/upgrade/preview', {
      plan_id: selectedPlan.id,
      billing_cycle: cycle,
    })
      .then((res) => setPreview(res.data?.data || null))
      .catch(() => setPreview(null));
  };

  if (loading) return <p className="text-muted-foreground">Loading plans…</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Compare Plans</h1>
          <p className="text-muted-foreground">Choose the best plan for your school</p>
        </div>
        <Button variant="outline" asChild><Link to="/settings/subscription">Back</Link></Button>
      </div>

      {showFreePlanCard && (
        <Card className="border-green-500/40 bg-green-500/10">
          <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium text-foreground">
                Current plan: <strong>{currentPlanName || user?.subscription?.plan_name || 'Free'}</strong> — active
              </p>
              <p className="text-muted-foreground mt-1">
                Your school account is ready to use. Upgrade below when you need more features.
              </p>
            </div>
            <Button variant="outline" className="shrink-0" asChild>
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {hasActiveSub && crmSuggestion?.plan && crmSuggestion.payment_status !== 'paid' && (
        <Card className="border-amber-500/40 bg-amber-500/10">
          <CardContent className="py-4 text-sm">
            <p className="font-medium text-foreground">
              Your sales team suggested <strong>{crmSuggestion.plan.name}</strong>
              {' '}({formatBillingCycleLabel(crmSuggestion.billing_cycle || 'monthly')})
            </p>
            <p className="text-muted-foreground mt-1">
              Upgrade when you&apos;re ready. You can select a different plan below.
              {crmSuggestion.amount > 0 && (
                <> Quoted amount: ₹{crmSuggestion.amount}.</>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {paymentPending && selectedPlan && (
        <Card className="border-amber-500/40 bg-amber-500/10">
          <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium text-foreground">
                {suggestedPlan && String(selectedPlan.id) === String(suggestedPlan.id) ? (
                  <>
                    Your sales team suggested <strong>{selectedPlan.name}</strong>
                    {' '}({formatBillingCycleLabel(selectedCycle || suggestedCycle)})
                  </>
                ) : (
                  <>
                    Selected plan: <strong>{selectedPlan.name}</strong>
                    {' '}({formatBillingCycleLabel(selectedCycle)})
                  </>
                )}
              </p>
              <p className="text-muted-foreground mt-1">
                Your sales team suggested this plan. Upgrade when you&apos;re ready.
                {crmSuggestion?.amount > 0 && (
                  <> Quoted amount: ₹{crmSuggestion.amount}.</>
                )}
                {' '}You can select a different plan below.
              </p>
            </div>
            <Button className="bg-blue-600 shrink-0" onClick={continueWithSelected}>
              Continue with selected plan
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.filter((p) => p.slug !== 'free').map((plan) => {
          const isCurrent = plan.id === currentPlanId;
          const isSelected = selectedPlanId && String(plan.id) === String(selectedPlanId);
          const isCrmSuggested = suggestedPlan && String(plan.id) === String(suggestedPlan.id);
          return (
            <div
              key={plan.id}
              ref={isSelected ? selectedPlanRef : undefined}
              className={
                isSelected
                  ? 'rounded-lg ring-2 ring-blue-500/60'
                  : isCrmSuggested
                    ? 'rounded-lg ring-2 ring-amber-500/40'
                    : undefined
              }
            >
              {isSelected && (
                <div className="px-2 pt-2">
                  <Badge variant="secondary" className="text-xs">
                    {isCrmSuggested && paymentPending ? 'Suggested & selected' : 'Selected'}
                  </Badge>
                </div>
              )}
              <PlanPricingCard
                plan={plan}
                cycle={isSelected ? (selectedCycle || 'monthly') : 'monthly'}
                isCurrent={isCurrent}
                actionLabel={isCurrent ? undefined : getActionLabel(plan)}
                onSelect={(p) => openBillingComparison(p, currentPlanId ? 'upgrade' : 'subscribe')}
                onRenew={(p) => openBillingComparison(p, 'renew')}
              />
            </div>
          );
        })}
      </div>

      <Card>
        <CardHeader><CardTitle>Feature Comparison</CardTitle></CardHeader>
        <CardContent><PlanFeatureMatrix plans={plans.filter((p) => p.slug !== 'free')} /></CardContent>
      </Card>

      <Dialog open={!!pendingPlan} onOpenChange={(open) => !open && closeBillingComparison()}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {pendingAction === 'renew' ? 'Renew' : pendingAction === 'upgrade' ? 'Upgrade' : 'Subscribe'} — Choose billing cycle
            </DialogTitle>
          </DialogHeader>
          {pendingPlan && (
            <BillingCycleComparison
              plan={pendingPlan}
              action={pendingAction}
              onSelectCycle={handleCycleSelect}
              onCancel={closeBillingComparison}
              showHeader={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmPlan} onOpenChange={(open) => !open && closeConfirm()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm {confirmPlan?.action === 'renew' ? 'Renewal' : confirmPlan?.action === 'subscribe' ? 'Subscription' : 'Upgrade'}</DialogTitle>
          </DialogHeader>
          {confirmPlan && (
            <div className="space-y-2 text-sm">
              <p><strong>Plan:</strong> {confirmPlan.plan.name}</p>
              <p><strong>Billing:</strong> {formatBillingCycleLabel(confirmPlan.cycle)}</p>
              {preview && (
                <>
                  <p><strong>New plan price:</strong> ₹{preview.new_plan_price}</p>
                  {preview.credit > 0 && <p><strong>Proration credit:</strong> -₹{preview.credit}</p>}
                  <p className="text-lg font-bold"><strong>Payable:</strong> ₹{preview.payable}</p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirm}>Cancel</Button>
            <Button className="bg-blue-600" onClick={checkout} disabled={checkingOut}>
              {checkingOut
                ? (preview?.payable <= 0 ? 'Activating…' : 'Opening Razorpay…')
                : (preview?.payable <= 0 ? 'Activate plan' : 'Pay with Razorpay')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
